import styled, {css, keyframes} from 'styled-components';
import {CoffeeMug} from '../cuteWidgets/olliePorch';
import {FELT} from '../cuteWidgets/felt';
import {OlliePlush} from '../mascot/OlliePlush';

/**
 * A cosy coffee shop in the mountains, bottom right of the screen while it
 * rains: a log cabin with a stone chimney and smoke drifting off it, warm
 * windows, a string of lights under the eave, pines beside it and a ridge
 * behind. Ollie sits on the porch bench with his coffee, enjoying the
 * weather. Decoration only: it takes no clicks, says nothing to screen
 * readers, and stays off narrow screens, where it would cover the page.
 *
 * The picture is two static SVGs that never repaint, and everything that
 * moves (smoke, bulbs, window glow, steam) is a small HTML element that
 * animates only transform and opacity, with soft edges from gradients
 * rather than SVG filters. Safari repaints an SVG on the CPU every frame
 * one of its children animates or carries a filter.
 */

/** Screens narrower than this get no café. */
const MIN_SCREEN_WIDTH = 900;

/** The scene's size on screen, in px; one viewBox unit is one px. */
const WIDTH = 300;
const HEIGHT = 230;

/** The café's colours. */
const LOG = '#B07C50';
const LOG_LINE = '#8E6139';
const ROOF = '#4B4643';
const ROOF_EDGE = '#5E5855';
const STONE = '#8C8780';
const STONE_DARK = '#6F6A64';
const WINDOW = '#FFD98A';
const FRAME = '#5A3C25';
const BENCH = '#6F4E37';
const PINE = '#2F6B4F';
const PINE_DARK = '#255740';
const RIDGE = '#9AA7B8';
const SNOW = '#EEF2F6';

/** The white disc behind the scene, so it reads the same on any page. */
const DISC = {cx: 152, cy: 122, r: 132} as const;

/** Where the chimney mouth is, in px within the scene. */
const CHIMNEY = {x: 214, y: 58} as const;

/** A smoke puff's size at full scale, in px. */
const PUFF_SIZE = 16;

/** The smoke puffs: each with its own sideways sway and start. */
const PUFFS = [
  {sway: -12, delaySec: 0},
  {sway: 16, delaySec: 1.2},
  {sway: -4, delaySec: 2.4},
  {sway: 20, delaySec: 3.6},
  {sway: 6, delaySec: 4.8},
  {sway: -16, delaySec: 6},
] as const;

/** Wisps of steam off the coffee: where each starts and when. */
const STEAM = [
  {x: 188, delaySec: 0},
  {x: 192, delaySec: 0.9},
  {x: 196, delaySec: 1.7},
] as const;

/** The string of lights under the eave, left to right. */
const LIGHTS = [
  {x: 78, color: '#FFD36B'},
  {x: 97.5, color: '#FF8FA3'},
  {x: 117, color: '#8FD3FF'},
  {x: 136.5, color: '#B5F28A'},
  {x: 156, color: '#FFD36B'},
  {x: 175.5, color: '#FF8FA3'},
  {x: 195, color: '#8FD3FF'},
  {x: 214.5, color: '#B5F28A'},
  {x: 234, color: '#FFD36B'},
] as const;

/** Where the bulbs hang, in px from the scene's top. */
const LIGHTS_Y = 131;

/** The big window's pane, in px within the scene. */
const PANE = {x: 154, y: 150, width: 58, height: 36} as const;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const breathe = keyframes`
  0%, 100% { opacity: 0.35; }
  50% { opacity: 0.75; }
`;

const twinkle = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
`;

const sip = keyframes`
  0%, 80%, 100% { transform: rotate(0deg) translateY(0); }
  88%, 94% { transform: rotate(-24deg) translateY(-3px); }
`;

const waft = keyframes`
  0% { opacity: 0; transform: translateY(2px); }
  25% { opacity: 0.9; }
  100% { opacity: 0; transform: translateY(-9px); }
`;

/**
 * Each puff's rise: born small at the chimney, it rises, swells, sways to
 * its own side and fades. One set of keyframes per puff, so no keyframe
 * reads a custom property and every engine can run it off the main thread.
 */
const PUFF_DRIFTS = PUFFS.map(
  puff => keyframes`
    0% { transform: translate(0, 0) scale(0.4); opacity: 0; }
    10% { opacity: 0.75; }
    60% { opacity: 0.45; }
    100% { transform: translate(${puff.sway}px, -96px) scale(2.6); opacity: 0; }
  `,
);

/** A pine tree, its trunk's foot at (x, y), `h` tall. */
function Pine({x, y, h}: {x: number; y: number; h: number}) {
  const w = h * 0.55;
  return (
    <g>
      <rect
        x={x - 2.5}
        y={y - h * 0.16}
        width={5}
        height={h * 0.16}
        fill={FRAME}
      />
      {[0.95, 0.68, 0.42].map((level, index) => (
        <path
          key={level}
          d={`M ${x} ${y - h * (level + 0.28)} L ${x + w * (0.35 + 0.22 * index)} ${y - h * (level - 0.3)} L ${x - w * (0.35 + 0.22 * index)} ${y - h * (level - 0.3)} Z`}
          fill={index === 1 ? PINE_DARK : PINE}
        />
      ))}
    </g>
  );
}

/** The scene behind the smoke: the disc, the ridge, pines and ground. */
function Backdrop() {
  return (
    <Layer viewBox={`0 0 ${WIDTH} ${HEIGHT}`} width={WIDTH} height={HEIGHT}>
      <circle
        cx={DISC.cx}
        cy={DISC.cy}
        r={DISC.r + 3}
        fill="#ffffff"
        opacity={0.6}
      />
      <circle cx={DISC.cx} cy={DISC.cy} r={DISC.r} fill="#ffffff" />
      <path
        d="M 0 150 L 40 96 L 72 118 L 118 70 L 160 110 L 206 62 L 250 104 L 300 80 L 300 160 L 0 160 Z"
        fill={RIDGE}
        opacity={0.45}
      />
      <g fill={SNOW} opacity={0.8}>
        <path d="M 118 70 L 128 82 L 118 80 L 108 82 Z" />
        <path d="M 206 62 L 216 76 L 206 73 L 196 76 Z" />
        <path d="M 40 96 L 48 106 L 40 104 L 32 106 Z" />
      </g>
      <Pine x={34} y={214} h={92} />
      <Pine x={62} y={216} h={62} />
      <Pine x={282} y={214} h={74} />
      <rect
        x={0}
        y={212}
        width={WIDTH}
        height={40}
        fill="#6E8B66"
        opacity={0.55}
      />
    </Layer>
  );
}

/** The cabin in front of the smoke: chimney, walls, windows, roof, porch. */
function Cabin() {
  return (
    <Layer viewBox={`0 0 ${WIDTH} ${HEIGHT}`} width={WIDTH} height={HEIGHT}>
      {/* Chimney. */}
      <rect x={204} y={62} width={20} height={46} fill={STONE} />
      <g fill={STONE_DARK}>
        <rect x={206} y={70} width={7} height={5} rx={1} />
        <rect x={215} y={78} width={7} height={5} rx={1} />
        <rect x={207} y={88} width={6} height={5} rx={1} />
        <rect x={216} y={96} width={6} height={5} rx={1} />
      </g>
      <rect x={201} y={58} width={26} height={6} rx={1} fill={STONE_DARK} />

      {/* Log walls. */}
      <rect x={74} y={118} width={160} height={88} fill={LOG} />
      {[128, 140, 152, 164, 176, 188, 200].map(y => (
        <path
          key={y}
          d={`M 74 ${y} H 234`}
          stroke={LOG_LINE}
          strokeWidth={1.4}
          opacity={0.8}
        />
      ))}
      <g fill={LOG_LINE} opacity={0.6}>
        {[123, 135, 147, 159, 171, 183, 195].map(y => (
          <g key={y}>
            <circle cx={72} cy={y + 1} r={3.2} />
            <circle cx={236} cy={y + 1} r={3.2} />
          </g>
        ))}
      </g>

      {/* Door, with a warm pane. */}
      <rect x={100} y={154} width={32} height={52} rx={3} fill={FRAME} />
      <rect x={105} y={160} width={22} height={22} rx={2} fill={WINDOW} />
      <circle cx={127} cy={190} r={1.8} fill={FELT.gold} />

      {/* The big window, with a flower box under it; its glow is a layer above. */}
      <rect x={150} y={146} width={66} height={44} rx={3} fill={FRAME} />
      <rect
        x={PANE.x}
        y={PANE.y}
        width={PANE.width}
        height={PANE.height}
        rx={2}
        fill={WINDOW}
      />
      <path
        d="M 183 150 V 186 M 154 168 H 212"
        stroke={FRAME}
        strokeWidth={2.4}
      />
      <rect x={146} y={190} width={74} height={4} rx={2} fill={FRAME} />
      <g fill={FELT.cardinal}>
        <circle cx={158} cy={193} r={2.4} />
        <circle cx={170} cy={193} r={2.4} />
        <circle cx={196} cy={193} r={2.4} />
        <circle cx={208} cy={193} r={2.4} />
      </g>

      {/* Hanging sign: a mug on a wooden board. */}
      <path
        d="M 134 118 V 128 M 148 118 V 128"
        stroke={FRAME}
        strokeWidth={1.2}
      />
      <rect x={128} y={128} width={26} height={16} rx={3} fill={FRAME} />
      <rect x={135} y={132} width={9} height={8} rx={1.5} fill={SNOW} />
      <path
        d="M 144 134 q 4 0 3 3 q -1 2 -3 2"
        stroke={SNOW}
        strokeWidth={1.4}
        fill="none"
      />

      {/* The roof: a gable with deep eaves. */}
      <path
        d="M 58 124 L 154 56 L 250 124 L 240 124 L 154 66 L 68 124 Z"
        fill={ROOF_EDGE}
      />
      <path
        d="M 66 122 L 154 60 L 242 122 L 232 122 L 154 70 L 76 122 Z"
        fill={ROOF}
      />
      <path
        d="M 154 56 L 250 124 L 240 124 L 154 66 Z"
        fill="#6B6562"
        opacity={0.6}
      />
      <path
        d="M 58 124 L 154 56 L 250 124"
        stroke="#3B3735"
        strokeWidth={2.4}
        fill="none"
        strokeLinejoin="round"
      />

      {/* The wire the string lights hang from; the bulbs are a layer above. */}
      <path
        d="M 68 124 Q 80 134 94 124 Q 108 134 122 124 Q 136 134 150 124 Q 164 134 178 124 Q 192 134 206 124 Q 220 134 234 124"
        stroke="#3B3735"
        strokeWidth={0.9}
        fill="none"
      />

      {/* Firewood stacked by the wall. */}
      <g fill="#C79A6B" stroke={FRAME} strokeWidth={0.8}>
        <circle cx={84} cy={200} r={4} />
        <circle cx={92} cy={200} r={4} />
        <circle cx={88} cy={193} r={4} />
      </g>

      {/* The porch deck and the bench's back; the seat is in front of Ollie. */}
      <rect x={62} y={206} width={184} height={6} rx={1} fill="#8B6B4A" />
      <rect x={160} y={176} width={54} height={5} rx={2} fill={BENCH} />
      <rect x={164} y={181} width={3} height={14} fill={BENCH} />
      <rect x={207} y={181} width={3} height={14} fill={BENCH} />
    </Layer>
  );
}

/**
 * The café, sized for a wide screen.
 *
 * @param props.isLeaving - True while the rain is stopping; the café fades
 *     out with it.
 * @param props.leaveMs - How long the fade out takes.
 * @param props.arriveMs - How long after the rain starts the café fades in.
 * @return The café.
 * @throws Never.
 */
export function RainCafe({
  isLeaving,
  leaveMs,
  arriveMs,
}: {
  isLeaving: boolean;
  leaveMs: number;
  arriveMs: number;
}) {
  return (
    <Cafe
      aria-hidden
      data-rain-cafe
      $isLeaving={isLeaving}
      $leaveMs={leaveMs}
      $arriveMs={arriveMs}
    >
      <Backdrop />
      {PUFFS.map((puff, index) => (
        <Puff
          key={puff.delaySec}
          $index={index}
          $sway={puff.sway}
          $delaySec={puff.delaySec}
        />
      ))}
      <Cabin />
      <Glow />
      {LIGHTS.map((light, index) => (
        <Bulb
          key={light.x}
          $x={light.x}
          $color={light.color}
          $delaySec={index * 0.37}
        />
      ))}
      <Ollie>
        <OlliePlush size={50} state="idle" />
        <Mug>
          <CoffeeMug />
        </Mug>
      </Ollie>
      <Seat viewBox={`0 0 ${WIDTH} ${HEIGHT}`} width={WIDTH} height={HEIGHT}>
        <rect x={158} y={194} width={58} height={7} rx={2.5} fill={BENCH} />
        <rect x={163} y={201} width={4} height={6} fill={BENCH} />
        <rect x={207} y={201} width={4} height={6} fill={BENCH} />
      </Seat>
      {/* Steam off the coffee; the mug's rim is at about (191, 180). */}
      {STEAM.map(wisp => (
        <Wisp key={wisp.x} $x={wisp.x} $delaySec={wisp.delaySec}>
          <svg viewBox="0 0 8 16" width={8} height={16}>
            <path
              d="M 4 15 q -2.5 -3 0 -6 q 2.5 -3 0 -6"
              fill="none"
              stroke="#ffffff"
              strokeWidth={1.4}
              strokeLinecap="round"
              opacity={0.9}
            />
          </svg>
        </Wisp>
      ))}
    </Cafe>
  );
}

const Cafe = styled.div<{
  $isLeaving: boolean;
  $leaveMs: number;
  $arriveMs: number;
}>`
  position: absolute;
  right: 16px;
  bottom: 22px;
  width: ${WIDTH}px;
  height: ${HEIGHT}px;
  pointer-events: none;
  animation: ${({$isLeaving, $leaveMs, $arriveMs}) =>
    $isLeaving
      ? css`
          ${fadeOut} ${$leaveMs}ms ease-in forwards
        `
      : css`
          ${fadeIn} 900ms ease-out ${$arriveMs}ms both
        `};

  @media (max-width: ${MIN_SCREEN_WIDTH - 1}px) {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    ${({$isLeaving}) => ($isLeaving ? 'opacity: 0;' : '')}
  }
`;

/** One of the scene's two static pictures. */
const Layer = styled.svg`
  position: absolute;
  left: 0;
  top: 0;
  overflow: visible;
`;

/** A puff of chimney smoke on its way up, soft-edged by its gradient. */
const Puff = styled.div<{$index: number; $sway: number; $delaySec: number}>`
  position: absolute;
  left: ${CHIMNEY.x - PUFF_SIZE / 2}px;
  top: ${CHIMNEY.y - PUFF_SIZE / 2}px;
  width: ${PUFF_SIZE}px;
  height: ${PUFF_SIZE}px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(180, 188, 200, 0.85) 0%,
    rgba(180, 188, 200, 0.5) 40%,
    rgba(180, 188, 200, 0) 70%
  );
  opacity: 0;
  animation: ${({$index, $delaySec}) => css`
    ${PUFF_DRIFTS[$index]} 7.2s ease-out ${$delaySec}s infinite
  `};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.5;
    transform: ${({$sway}) => `translate(${$sway / 3}px, -30px) scale(1.6)`};
  }
`;

/** The window's warm lamplight, breathing a little over the pane. */
const Glow = styled.div`
  position: absolute;
  left: ${PANE.x}px;
  top: ${PANE.y}px;
  width: ${PANE.width}px;
  height: ${PANE.height}px;
  border-radius: 2px;
  background: radial-gradient(
    ellipse at center,
    rgba(255, 240, 190, 0.95),
    rgba(255, 217, 138, 0) 75%
  );
  box-shadow: 0 0 12px 4px rgba(255, 205, 110, 0.35);
  opacity: 0.35;
  animation: ${breathe} 3.2s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.55;
  }
`;

/** One bulb on the string, twinkling in its own time. */
const Bulb = styled.div<{$x: number; $color: string; $delaySec: number}>`
  position: absolute;
  left: ${({$x}) => $x - 2.2}px;
  top: ${LIGHTS_Y - 2.2}px;
  width: 4.4px;
  height: 4.4px;
  border-radius: 50%;
  background: ${({$color}) => $color};
  box-shadow: 0 0 4px 1px ${({$color}) => $color};
  animation: ${twinkle} 2.6s ease-in-out ${({$delaySec}) => $delaySec}s infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * Ollie on the bench, between its back and its seat. He sits still: the
 * plush's idle breath and blink animate inside its fuzz-filtered SVG, which
 * Safari re-filters on the CPU every frame. Only the mug's sip, an HTML
 * transform, moves; the mug's own steam is replaced by the wisps.
 */
const Ollie = styled.div`
  position: absolute;
  left: 162px;
  bottom: 30px;
  width: 50px;
  z-index: 1;

  svg * {
    animation: none !important;
  }
`;

/** The bench's seat, in front of Ollie so he sits in it. */
const Seat = styled.svg`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 2;
`;

/** One wisp of steam off the coffee, rising and fading, over and over. */
const Wisp = styled.div<{$x: number; $delaySec: number}>`
  position: absolute;
  left: ${({$x}) => $x - 4}px;
  top: 164px;
  z-index: 3;
  opacity: 0;
  animation: ${waft} 2.6s ease-out ${({$delaySec}) => $delaySec}s infinite;

  svg {
    display: block;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.7;
  }
`;

/** The mug in Ollie's paw, lifted for a sip now and then. */
const Mug = styled.div`
  position: absolute;
  left: 44%;
  top: 52%;
  width: 30%;
  transform-origin: center bottom;
  animation: ${sip} 7s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
